import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  useMemo,
} from "react";
import { UserInfoProviderContext } from "../../../shared/services/api/contexts";
import { useSnackbar } from "../../../shared/components/layout/SnackbarContext";
import { Permission, User } from "../../../models/auth";

interface UserContextType {
  user: User | null;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  refreshUser: () => void;
}

export const UserContext = createContext<UserContextType>(null as unknown as UserContextType);

type UserProviderProps = {
  children: ReactNode;
};

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const { showSnackbar } = useSnackbar();
  const auth = useContext(UserInfoProviderContext);
  const [user, setUser] = useState<User | null>(null);

  const getUser = useCallback(async () => {
    try {
      if (window.DISABLE_AUTH) {
        setUser({
          email: "test@resilience.care",
          attributes: {
            given_name: "Michel",
            name: "D",
            organization: "Resilience",
            permissions: [
              Permission.PATIENT,
              Permission.DOCUMENT,
              Permission.RPM,
              Permission.COHORT,
              Permission.PROJECT,
              Permission.ADMIN,
              Permission.IDENTIFYING_DATA,
            ],
          },
        });
      } else {
        const user = await auth.getUser();
        setUser(user);
      }
    } catch (e: any) {
      showSnackbar({ text: e.message, color: "danger" });
    }
  }, [auth, showSnackbar]);

  const refreshUser = useCallback(async () => {
    getUser();
  }, [getUser]);

  useEffect(() => {
    getUser();
  }, [getUser]);

  const value = useMemo(() => ({ user, setUser, refreshUser }), [user, setUser, refreshUser]);

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};
